//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// login-default imports
import * as s from "../LoginWrapper.styles";
import { loginDefaultFragments } from "./LoginDefault.gql";
import { IContainer, IProps, IState, IWrapper } from "./LoginDefaultTypes";
// other internals
import { LoginBtn } from "#webapp/src/components/login-btn";
import { MsftLoginButton } from "#webapp/src/components/msft-login-button";
import { CookieDialog } from "@clockwise/web-commons/src/components/cookie-dialog";
// img
// util
import { page, PageEvents, track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
// constants
import { paths } from "#webapp/src/constants/site.constant";
// state
import { loginUser } from "#webapp/src/mutations";
import { hasSeenLoginPage, lastSignInDate } from "#webapp/src/state/local-storage";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
// material-ui
import { useFeatureFlag } from "#webapp/src/launch-darkly/useLaunchDarkly";
import { Link } from "@clockwise/design-system";
import { LogoClock } from "@clockwise/web-commons/src/components/logo";
import { LinearProgress } from "@material-ui/core";
import { DateTime } from "luxon";
import Helmet from "react-helmet";

//////////////////
// COMPONENT
//////////////////
class LoginDefaultCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      loading: false,
      hideCookieErrorDialog: true,
    };
    this.onLoginStart = this.onLoginStart.bind(this);
    this.onLoginSuccess = this.onLoginSuccess.bind(this);
    this.onLoginFailure = this.onLoginFailure.bind(this);
  }

  public componentDidMount() {
    page(PageEvents.LOGIN);
    hasSeenLoginPage.set(true);
    if (this.props.isChromeExtension) {
      PostMessageManager.openSidebar(true);
    }
  }

  private onLoginStart = () => {
    track(TrackingEvents.AUTH.BEGIN_LOGIN);
    this.setState({ loading: true });
  };

  private onLoginSuccess = () => {
    lastSignInDate.set(DateTime.now().toISO());
    this.props.onSuccess();
  };

  private onLoginFailure = (_e: any) => {
    track(TrackingEvents.AUTH.LOGIN_FAILED);
    this.setState({ loading: false });
  };

  private onGoogleFailure = (e: any) => {
    if (e.error === "idpiframe_initialization_failed") {
      this.setState({
        hideCookieErrorDialog: false,
      });
    }
  };

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    // detect login success and redirect using current org experiments
    if (nextProps.viewer.user && !this.props.viewer.user) {
      this.setState({ loading: false });
      this.forceUpdate();
    }
  }

  private renderCookieDialog() {
    return (
      <CookieDialog
        isChromeExtension={this.props.isChromeExtension}
        open={!this.state.hideCookieErrorDialog}
      />
    );
  }

  public render() {
    return (
      <React.Fragment>
        <Helmet>
          <title>Sign in to Clockwise · Clockwise</title>
        </Helmet>
        {this.renderCookieDialog()}
        <div className="cw-p-2 cw-flex cw-items-center cw-justify-center cw-h-full cw-flex-1">
          <div className="cw-bg-default cw-rounded-lg cw-w-[480px] cw-p-[60px] cw-flex cw-flex-col cw-items-center cw-relative">
            <div className="cw-w-[95px] cw-h-[95px] cw-flex cw-items-center cw-justify-center cw-rounded-full cw-flex-shrink-0 cw-bg-accent-emphasis">
              <LogoClock fill="bright" className="cw-w-[60px] cw-h-[60px]" />
            </div>
            <div className="cw-flex cw-flex-col cw-items-center cw-p-2 cw-pt-4">
              <div className="cw-heading-base cw-mb-4 cw-text-center">
                Sign in to Clockwise with your work calendar
              </div>
              <div className="cw-w-[215px] cw-mb-4">
                <LoginBtn
                  label="Sign in with Google"
                  loginUserMutation={loginUser}
                  style={s.signupBtn.base}
                  labelStyle={s.signupBtn.label}
                  redirect={this.props.redirect}
                  onStart={this.onLoginStart}
                  onSuccess={this.onLoginSuccess}
                  onFailure={this.onLoginFailure}
                  onGoogleFailure={this.onGoogleFailure}
                />
              </div>
              {this.props.isOnM365BetaRelaunch ? (
                <div className="cw-w-full cw-mb-4 cw-flex cw-items-center cw-w-5">
                  <div className="cw-grow cw-border-b cw-border-solid cw-border-muted"></div>
                  <div className="cw-px-2 cw-caption cw-text-muted cw-text-center">
                    or try out our latest beta
                  </div>
                  <div className="cw-grow cw-border-b cw-border-solid cw-border-muted"></div>
                </div>
              ) : (
                ""
              )}
              <div className="cw-w-[215px] cw-mb-4">
                <MsftLoginButton redirect={this.props.redirect} />
              </div>
            </div>
            <div className="cw-caption cw-text-muted cw-text-center">
              By signing in, you agree to Clockwise's{" "}
              <Link target="_blank" href={paths.terms}>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link target="_blank" href={paths.privacy}>
                Privacy Policy
              </Link>
              .
            </div>
            {this.state.loading && (
              <LinearProgress
                className="cw-absolute cw-bottom-0 cw-w-full cw-bg-transparent"
                variant="indeterminate"
              />
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    isChromeExtension: state.webExtension.isWebExtension,
  };
}

const reduxContainer = connect(mapStateToProps, {})(LoginDefaultCmpt);

const LoginDefaultRelay = createFragmentContainer<IContainer>(
  reduxContainer,
  loginDefaultFragments,
);

export const LoginDefault = (props: IWrapper) => {
  const [isOnM365BetaRelaunch] = useFeatureFlag("M365BetaRelaunch");

  return <LoginDefaultRelay {...props} isOnM365BetaRelaunch={isOnM365BetaRelaunch} />;
};
